import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import {
  ChevronDown, ArrowRight, Factory, ClipboardList, Wrench, Home,
  ChevronUp,
  Check, X, Phone, Mail, Globe, MapPin, Send, Loader
} from 'lucide-react';
import PortfolioSection from './components/portfolio/PortfolioSection';

// ============================================================
//  SHARED DATA
// ============================================================
const SECTORS = [
  'FOOD','AUTOMOBILE','WAREHOUSES','FMCG','PHARMA',
  'MECHANICAL','HEAVY ENGG.','BEVERAGES',
  'INSTITUTIONAL','RESIDENTIAL','COMMERCIAL','INFRA WORKS',
];

const SERVICES = [
  {
    id: 'industrial', icon: Factory, number: '01',
    title: 'Industrial Projects',
    subtitle: 'Manufacturing & Industrial Facilities',
    desc: 'Deep expertise in industrial construction spanning multiple high-demand sectors — from food-grade hygiene standards to heavy-load structural demands.',
    sectors: ['Food Processing','Automobile Manufacturing','Warehouses & Logistics','FMCG Facilities','Pharma Plants','Mechanical Engineering','Heavy Engineering','Beverage Industry'],
    img: 'https://images.unsplash.com/photo-1694885169342-909981fb408a?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  },
  {
    id: 'civil', icon: Home, number: '02',
    title: 'Institutional, Residential & Infra',
    subtitle: 'Civil Construction Works',
    desc: 'Comprehensive construction services for institutional campuses, residential developments, commercial complexes and infrastructure works within premises.',
    sectors: ['Institutional Buildings','Residential Complexes','Commercial Spaces','Infrastructure Works','Internal Roads & Drainage','Boundary Walls & Security','Utility Installations','Site Development'],
    img: 'https://images.unsplash.com/photo-1549791084-5f78368b208b?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  },
  {
    id: 'pm', icon: ClipboardList, number: '03',
    title: 'Project Management',
    subtitle: 'Pre-Construction | During | Post-Construction',
    desc: 'End-to-end project management with defined SOPs, checklists and quality management plans — from concept to project closure.',
    phases: [
      { phase: 'PRE-CONSTRUCTION', items: ['Reconnaissance Survey','Consultant Selection','Design Management','Scheduling','Budgeting','Pre-Qualification of Contractors','Tendering','HSE Planning'] },
      { phase: 'DURING CONSTRUCTION', items: ['Co-ordination','Logistics Review','Execution Oversight','QA/QC Management'] },
      { phase: 'POST CONSTRUCTION', items: ['Snagging & De-Snagging','Bill Checking & Certification','Reporting','Project Closure'] },
    ],
    img: 'https://images.unsplash.com/photo-1778074762022-c33cc42f79ae?crop=entropy&cs=srgb&fm=jpg&w=800&q=80',
  },
  {
    id: 'premises', icon: Wrench, number: '04',
    title: 'Premises Management & EPC',
    subtitle: 'Facilities, EPC & Turnkey Solutions',
    desc: 'Post-handover facility management and comprehensive EPC / Turnkey contracts from design brief to final handover — a single point of accountability.',
    sectors: ['Landscaping & Horticulture','Housekeeping Services','Facility Management','Routine Maintenance','EPC — Engineering, Procurement, Construction','Turnkey Cost+ (Design to Handover)','Vendor Management','Annual Maintenance Contracts'],
    img: 'https://images.pexels.com/photos/17265232/pexels-photo-17265232.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
];

const PDCA = [
  {
    number: '01', phase: 'PLAN', label: 'P', color: '#C41E3A',
    tagline: 'Lay the Foundation',
    items: ['Reconnaissance Survey','Consultant Selection','Design Management','Project Scheduling','Budgeting & Cost Planning','Pre-Qualification of Contractors','Tendering & Vendor Selection','Health, Safety & Environment (HSE)'],
  },
  {
    number: '02', phase: 'DO / DESIGN', label: 'D', color: '#FFFFFF',
    tagline: 'Execute with Precision',
    items: ['Team Co-ordination','Logistics Review & Planning','Construction Execution','Quality Assurance & Quality Control','Change Order Tracking','Daily Progress Monitoring','Stakeholder Reporting','Site Safety Enforcement'],
  },
  {
    number: '03', phase: 'CHECK', label: 'C', color: '#C41E3A',
    tagline: 'Validate Every Detail',
    items: ['Snagging & De-Snagging','Bill Checking & Certification','Progress Reporting','Quality Management Plan','QAQC Inspections','Specification Compliance Review','Checklist Verification','Documentation Audit'],
  },
  {
    number: '04', phase: 'ACT', label: 'A', color: '#FFFFFF',
    tagline: 'Deliver & Improve',
    items: ['Project Closure','Final Handover','Lessons Learned Documentation','Continuous Improvement','SOP Updates','Client Satisfaction Review','Post-Construction Support','Process Refinement'],
  },
];

const COMPARISON = [
  { param: 'Design & Flexibility', peb: 'Pre-designed and factory-fabricated — faster and more flexible designs.', conv: 'Customized designs but require more time for planning and approvals.', pebWins: true },
  { param: 'Construction Time', peb: 'Faster due to prefabrication; significantly reduces on-site work.', conv: 'Slower — components are fabricated and assembled entirely on-site.', pebWins: true },
  { param: 'Cost Efficiency', peb: 'Cost-effective due to standardization and faster completion.', conv: 'Higher costs due to longer timelines and material wastage.', pebWins: true },
  { param: 'Material Usage', peb: 'Optimized steel usage ensures minimal material wastage.', conv: 'Higher material wastage and less efficient use of steel and concrete.', pebWins: true },
  { param: 'Durability & Maintenance', peb: 'High durability with low maintenance due to precise manufacturing.', conv: 'Durable but requires regular maintenance, especially exposed steel.', pebWins: true },
  { param: 'Aesthetic Appeal', peb: 'Limited aesthetic options due to standardized designs.', conv: 'Greater scope for intricate and fully customized designs.', pebWins: false },
  { param: 'Sustainability', peb: 'Eco-friendly with recyclable materials and energy-efficient construction.', conv: 'Generates more construction waste; less sustainable overall.', pebWins: true },
  { param: 'Load Handling', peb: 'Ideal for uniform loads — warehouses, factories, large spans.', conv: 'Better suited for complex structures with variable load requirements.', pebWins: false },
];

const FRAMES = [
  {
    code: 'TCCS', name: 'Twin Chord Clear Span',
    desc: 'Single-bay gabled portal frame. Ideal for wide, open industrial spaces.',
    svg: (
      <svg viewBox="0 0 280 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
        <line x1="10" y1="128" x2="270" y2="128" stroke="#4A4A4A" strokeWidth="1" strokeDasharray="5 3"/>
        <line x1="50" y1="128" x2="50" y2="78" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="50" y1="78" x2="62" y2="64" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="62" y1="64" x2="140" y2="22" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="140" y1="22" x2="218" y2="64" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="218" y1="64" x2="230" y2="78" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="230" y1="78" x2="230" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="37" y1="128" x2="63" y2="128" stroke="#C41E3A" strokeWidth="3"/>
        <line x1="217" y1="128" x2="243" y2="128" stroke="#C41E3A" strokeWidth="3"/>
        <line x1="140" y1="22" x2="140" y2="12" stroke="#FFFFFF" strokeWidth="0.5"/>
        <text x="145" y="10" fill="#9CA3AF" fontSize="8" fontFamily="sans-serif">RIDGE</text>
        <text x="10" y="142" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">FFL</text>
        <text x="95" y="145" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">BUILDING WIDTH</text>
      </svg>
    ),
  },
  {
    code: 'TCMS-1', name: 'Twin Column Multi-Span 1',
    desc: 'Two-bay portal frame with central column. Extends span capability.',
    svg: (
      <svg viewBox="0 0 280 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
        <line x1="10" y1="128" x2="270" y2="128" stroke="#4A4A4A" strokeWidth="1" strokeDasharray="5 3"/>
        <line x1="30" y1="128" x2="30" y2="80" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="30" y1="80" x2="38" y2="70" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="38" y1="70" x2="85" y2="28" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="85" y1="28" x2="132" y2="70" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="132" y1="70" x2="140" y2="80" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="140" y1="80" x2="140" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="140" y1="80" x2="148" y2="70" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="148" y1="70" x2="195" y2="28" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="195" y1="28" x2="242" y2="70" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="242" y1="70" x2="250" y2="80" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="250" y1="80" x2="250" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="18" y1="128" x2="42" y2="128" stroke="#C41E3A" strokeWidth="3"/>
        <line x1="128" y1="128" x2="152" y2="128" stroke="#C41E3A" strokeWidth="3"/>
        <line x1="238" y1="128" x2="262" y2="128" stroke="#C41E3A" strokeWidth="3"/>
        <text x="10" y="142" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">FFL</text>
        <text x="95" y="145" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">BUILDING WIDTH</text>
      </svg>
    ),
  },
  {
    code: 'MGCS', name: 'Multi-Gable Column Section',
    desc: 'Three-bay multi-gable frame with multiple ridges for large footprints.',
    svg: (
      <svg viewBox="0 0 280 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
        <line x1="10" y1="128" x2="270" y2="128" stroke="#4A4A4A" strokeWidth="1" strokeDasharray="5 3"/>
        <line x1="20" y1="128" x2="20" y2="84" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="20" y1="84" x2="55" y2="30" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="55" y1="30" x2="90" y2="84" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="90" y1="84" x2="90" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="90" y1="84" x2="125" y2="30" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="125" y1="30" x2="160" y2="84" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="160" y1="84" x2="160" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="160" y1="84" x2="195" y2="30" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="195" y1="30" x2="230" y2="84" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="230" y1="84" x2="230" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        {[20,90,160,230].map(x => <line key={x} x1={x-7} y1="128" x2={x+7} y2="128" stroke="#C41E3A" strokeWidth="3"/>)}
        <text x="10" y="142" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">FFL</text>
        <text x="95" y="145" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">BUILDING WIDTH</text>
      </svg>
    ),
  },
  {
    code: 'TCMS-2', name: 'Twin Column Multi-Span 2',
    desc: 'Three-bay portal with two interior columns. Balanced multi-span solution.',
    svg: (
      <svg viewBox="0 0 280 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
        <line x1="10" y1="128" x2="270" y2="128" stroke="#4A4A4A" strokeWidth="1" strokeDasharray="5 3"/>
        <line x1="20" y1="128" x2="20" y2="78" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="20" y1="78" x2="27" y2="68" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="27" y1="68" x2="63" y2="26" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="63" y1="26" x2="97" y2="68" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="97" y1="68" x2="103" y2="78" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="103" y1="78" x2="103" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="103" y1="78" x2="110" y2="68" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="110" y1="68" x2="140" y2="26" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="140" y1="26" x2="170" y2="68" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="170" y1="68" x2="177" y2="78" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="177" y1="78" x2="177" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="177" y1="78" x2="183" y2="68" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="183" y1="68" x2="218" y2="26" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="218" y1="26" x2="253" y2="68" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="253" y1="68" x2="260" y2="78" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="260" y1="78" x2="260" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        {[20,103,177,260].map(x => <line key={x} x1={x-7} y1="128" x2={x+7} y2="128" stroke="#C41E3A" strokeWidth="3"/>)}
        <text x="10" y="142" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">FFL</text>
        <text x="100" y="145" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">BUILDING WIDTH</text>
      </svg>
    ),
  },
  {
    code: 'SSCS', name: 'Single Slope Clear Span',
    desc: 'Single-slope mono-pitch frame for lean-to structures and attachment buildings.',
    svg: (
      <svg viewBox="0 0 280 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
        <line x1="10" y1="128" x2="270" y2="128" stroke="#4A4A4A" strokeWidth="1" strokeDasharray="5 3"/>
        <line x1="40" y1="128" x2="40" y2="58" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="40" y1="58" x2="240" y2="88" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="240" y1="88" x2="240" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="27" y1="128" x2="53" y2="128" stroke="#C41E3A" strokeWidth="3"/>
        <line x1="227" y1="128" x2="253" y2="128" stroke="#C41E3A" strokeWidth="3"/>
        <text x="10" y="142" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">FFL</text>
        <text x="95" y="145" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">BUILDING WIDTH</text>
      </svg>
    ),
  },
  {
    code: 'TCMS-3', name: 'Twin Column Multi-Span 3',
    desc: 'Four-bay portal frame. High-capacity multi-span for large industrial complexes.',
    svg: (
      <svg viewBox="0 0 280 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
        <line x1="10" y1="128" x2="270" y2="128" stroke="#4A4A4A" strokeWidth="1" strokeDasharray="5 3"/>
        {[14,76,138,202,266].map((x,i) => <line key={i} x1={x} y1="128" x2={x} y2="82" stroke="#C41E3A" strokeWidth="2"/>)}
        {[14,76,138,202].map((x,i) => {
          const mid = x + 31;
          return (<g key={i}><line x1={x} y1="82" x2={mid} y2="30" stroke="#C41E3A" strokeWidth="2"/><line x1={mid} y1="30" x2={x+62} y2="82" stroke="#C41E3A" strokeWidth="2"/></g>);
        })}
        {[14,76,138,202,266].map(x => <line key={x} x1={x-5} y1="128" x2={x+5} y2="128" stroke="#C41E3A" strokeWidth="3"/>)}
        <text x="10" y="142" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">FFL</text>
        <text x="100" y="145" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">BUILDING WIDTH</text>
      </svg>
    ),
  },
  {
    code: 'Y-CANOPY', name: 'Y-Canopy Frame',
    desc: 'Y-shaped canopy support structure for shade or entry canopy applications.',
    svg: (
      <svg viewBox="0 0 280 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
        <line x1="10" y1="128" x2="270" y2="128" stroke="#4A4A4A" strokeWidth="1" strokeDasharray="5 3"/>
        <line x1="140" y1="128" x2="140" y2="75" stroke="#C41E3A" strokeWidth="2.5"/>
        <line x1="140" y1="75" x2="60" y2="45" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="140" y1="75" x2="220" y2="45" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="50" y1="42" x2="80" y2="42" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="210" y1="42" x2="240" y2="42" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="127" y1="128" x2="153" y2="128" stroke="#C41E3A" strokeWidth="3"/>
        <text x="10" y="142" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">FFL</text>
        <text x="110" y="155" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">Y-CANOPY</text>
      </svg>
    ),
  },
  {
    code: 'TOP RUNNING CRANE', name: 'Top Running Crane Frame',
    desc: 'Portal frame with integrated crane beam for material handling within the structure.',
    svg: (
      <svg viewBox="0 0 280 150" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
        <line x1="10" y1="128" x2="270" y2="128" stroke="#4A4A4A" strokeWidth="1" strokeDasharray="5 3"/>
        <line x1="35" y1="128" x2="35" y2="30" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="35" y1="30" x2="245" y2="30" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="245" y1="30" x2="245" y2="128" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="35" y1="70" x2="52" y2="70" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="245" y1="70" x2="228" y2="70" stroke="#C41E3A" strokeWidth="2"/>
        <line x1="52" y1="70" x2="228" y2="70" stroke="#FFFFFF" strokeWidth="1.5"/>
        <line x1="100" y1="70" x2="180" y2="70" stroke="#C41E3A" strokeWidth="2.5"/>
        <line x1="140" y1="70" x2="140" y2="90" stroke="#FFFFFF" strokeWidth="1.5"/>
        <rect x="133" y="90" width="14" height="10" stroke="#FFFFFF" strokeWidth="1" fill="none"/>
        <line x1="22" y1="128" x2="48" y2="128" stroke="#C41E3A" strokeWidth="3"/>
        <line x1="232" y1="128" x2="258" y2="128" stroke="#C41E3A" strokeWidth="3"/>
        <text x="82" y="66" fill="#9CA3AF" fontSize="6" fontFamily="sans-serif">CRANE BEAM</text>
        <text x="10" y="142" fill="#9CA3AF" fontSize="7" fontFamily="sans-serif">FFL</text>
      </svg>
    ),
  },
];

// PROJECTS data moved to /src/data/portfolioData.js

// ============================================================
//  SECTION COMPONENTS
// ============================================================

/* ---------- HERO ---------- */
const HeroSection = () => (
  <section
    id="hero"
    className="relative min-h-screen flex items-center"
    style={{ background: `linear-gradient(rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.55) 60%, rgba(10,10,10,1) 100%), url(https://images.pexels.com/photos/13261149/pexels-photo-13261149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920) center/cover no-repeat` }}
    data-testid="hero-section"
  >
    <div className="absolute inset-0 grid-pattern opacity-25 pointer-events-none" />
    <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-24">
      <p className="section-label mb-6 fade-up" data-testid="hero-label">Construction &amp; Project Management</p>
      <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl xl:text-9xl text-white leading-none mb-6 fade-up delay-1" data-testid="hero-tagline">
        WHERE<br />INNOVATION<br /><span className="text-[#C41E3A]">MEETS</span><br />INTEGRITY
      </h1>
      <p className="text-[#D1D5DB] text-base sm:text-lg max-w-xl mb-10 fade-up delay-2 font-body">
        Kadam Patil Projects — delivering industrial, commercial and institutional construction with in-depth expertise, loyalty and precision.
      </p>
      <div className="flex flex-wrap gap-4 mb-16 fade-up delay-3">
        <a href="#portfolio" data-testid="hero-cta-portfolio" className="bg-[#C41E3A] text-white text-xs font-bold uppercase tracking-[0.2em] px-8 py-4 clip-btn hover:bg-white hover:text-[#0A0A0A] transition-colors duration-200">
          VIEW OUR WORK
        </a>
        <a href="#contact" data-testid="hero-cta-contact" className="border border-[#4A4A4A] text-white text-xs font-bold uppercase tracking-[0.2em] px-8 py-4 hover:border-[#C41E3A] hover:text-[#C41E3A] transition-colors duration-200">
          GET IN TOUCH
        </a>
      </div>
      <div className="flex flex-wrap gap-8 lg:gap-16 fade-up delay-4" data-testid="hero-stats">
        {[{ v:'11', l:'Projects Delivered' },{ v:'1 LAC', l:'Sq. Ft. Completed' },{ v:'4+', l:'Years Experience' }].map(({ v, l }) => (
          <div key={l} className="border-l-2 border-[#C41E3A] pl-5">
            <div className="font-heading text-3xl lg:text-4xl text-white">{v}</div>
            <div className="text-[#9CA3AF] text-xs uppercase tracking-widest mt-1 font-body">{l}</div>
          </div>
        ))}
      </div>
    </div>
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 scroll-indicator">
      <span className="text-[#9CA3AF] text-xs uppercase tracking-widest font-body">Scroll</span>
      <ChevronDown size={18} strokeWidth={1.5} className="text-[#C41E3A]" />
    </div>
  </section>
);

/* ---------- TICKER ---------- */
const TickerSection = () => (
  <div className="bg-[#C41E3A] py-4 overflow-ticker border-y border-[#C41E3A]" data-testid="sector-ticker">
    <div className="ticker-track">
      {[...SECTORS, ...SECTORS].map((s, i) => (
        <span key={i} className="inline-flex items-center gap-4 px-6 font-heading text-white text-sm tracking-widest whitespace-nowrap">
          {s}<span className="w-1.5 h-1.5 bg-white/50 rounded-full" />
        </span>
      ))}
    </div>
  </div>
);

/* ---------- SERVICES ---------- */
const ServiceCard = ({ service }) => {
  const [expanded, setExpanded] = useState(false);
  const { icon: Icon, number, title, subtitle, desc, sectors, phases, img, id } = service;
  return (
    <div className="border-r border-[#4A4A4A] last:border-r-0 bg-[#0A0A0A] hover:bg-[#0D0D0D] group flex flex-col" data-testid={`service-card-${id}`}>
      <div className="relative h-40 overflow-hidden shrink-0">
        <img src={img} alt={title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-black/40 to-transparent" />
        <div className="absolute top-3 left-4 font-heading text-5xl text-white/8">{number}</div>
      </div>
      <div className="p-7 flex flex-col flex-1">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-9 h-9 border border-[#4A4A4A] flex items-center justify-center shrink-0 group-hover:border-[#C41E3A] transition-colors">
            <Icon size={16} strokeWidth={1.5} className="text-[#C41E3A]" />
          </div>
          <div>
            <h3 className="font-heading text-xl text-white leading-tight">{title}</h3>
            <p className="text-[#C41E3A] text-xs uppercase tracking-widest font-body mt-0.5">{subtitle}</p>
          </div>
        </div>
        <p className="text-[#9CA3AF] text-sm leading-relaxed font-body mb-4 flex-1">{desc}</p>
        <button
          className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#D1D5DB] hover:text-[#C41E3A] transition-colors font-body font-medium mt-auto"
          onClick={() => setExpanded(!expanded)}
          data-testid={`service-expand-${id}`}
        >
          {expanded ? 'Show Less' : 'View Details'}
          {expanded ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
        </button>
        {expanded && (
          <div className="mt-5 pt-4 border-t border-[#4A4A4A]">
            {phases ? (
              <div className="space-y-4">
                {phases.map(({ phase, items }) => (
                  <div key={phase}>
                    <p className="text-[#C41E3A] text-xs uppercase tracking-widest font-body font-medium mb-2">{phase}</p>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                      {items.map(item => <div key={item} className="flex items-center gap-2 text-[#D1D5DB] text-xs font-body"><span className="w-1 h-1 bg-[#C41E3A] rounded-full shrink-0"/>{item}</div>)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                {sectors.map(s => <div key={s} className="flex items-center gap-2 text-[#D1D5DB] text-xs font-body"><span className="w-1 h-1 bg-[#C41E3A] rounded-full shrink-0"/>{s}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ServicesSection = () => (
  <section id="services" className="py-24 bg-[#0A0A0A]" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-14 gap-6">
        <div>
          <p className="section-label mb-4">Our Expertise</p>
          <h2 className="font-heading text-5xl lg:text-6xl text-white">WHAT WE BUILD</h2>
        </div>
        <p className="text-[#9CA3AF] text-sm font-body max-w-sm">EPC Contracts &bull; Turnkey Cost+ &bull; Project Management &bull; Pre &amp; Post Construction</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#4A4A4A] border border-[#4A4A4A]">
        {SERVICES.map(s => <ServiceCard key={s.id} service={s} />)}
      </div>
    </div>
  </section>
);

/* ---------- ABOUT ---------- */
const AboutSection = () => (
  <section id="about" className="py-24 bg-[#0D0D0D] border-y border-[#4A4A4A]" data-testid="about-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      {/* Header */}
      <div className="mb-16">
        <p className="section-label mb-4">Who We Are</p>
        <h2 className="font-heading text-5xl lg:text-6xl text-white">ABOUT US</h2>
      </div>

      {/* About + Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16 items-start">
        <div>
          <p className="text-[#D1D5DB] text-base leading-relaxed mb-5 font-body">
            We Kadam Patil Projects are a young and emerging Construction and Management firm equipped with modern-edge technology and education to organise the construction industry. We love to work with in-depth expertise in any field by really collaborating with the concerned expert with loyalty for our clients.
          </p>
          <p className="text-[#9CA3AF] text-sm leading-relaxed font-body">
            We claim this on the basis of 1 Lac Sq. Ft. of experience. We are proactive towards the successful completion of projects and satisfying client expectations. To achieve the objectives of projects, we are flexible, innovative and adaptive to any situation. We also help our clients for pre and post construction services.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-px bg-[#4A4A4A]">
          {[{ n:'11', l:'Projects Delivered' },{ n:'1 LAC', l:'Sq. Ft. Experience' },{ n:'4+', l:'Years Active' },{ n:'100%', l:'Client Dedication' }].map(({ n, l }) => (
            <div key={l} className="bg-[#0D0D0D] p-8 hover:bg-[#1A1A1A] transition-colors">
              <div className="font-heading text-4xl text-[#C41E3A] mb-1">{n}</div>
              <div className="text-[#9CA3AF] text-xs uppercase tracking-widest font-body">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Vision & Mission */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#4A4A4A] mb-16">
        <div className="bg-[#0A0A0A] p-10 relative">
          <div className="absolute top-0 left-0 w-full h-0.5 bg-[#C41E3A]" />
          <p className="section-label mb-4">Our Vision</p>
          <h3 className="font-heading text-3xl text-white mb-5">VISION</h3>
          <p className="text-[#D1D5DB] text-base leading-relaxed font-body">
            To be the <span className="text-white font-medium">first preferred</span> construction and management company and make the industry organised in every manner.
          </p>
        </div>
        <div className="bg-[#1A1A1A] p-10">
          <p className="section-label mb-4">Our Mission</p>
          <h3 className="font-heading text-3xl text-white mb-5">MISSION</h3>
          <p className="text-[#D1D5DB] text-sm leading-relaxed font-body">
            Working with loyalty builds strong relationships and trust. Striving for excellence motivates us to reach our highest potential, while innovating and adapting enables us to tackle challenges effectively. Together, these principles create a thriving and dynamic environment.
          </p>
        </div>
      </div>

      {/* Team */}
      <div>
        <div className="mb-12 max-w-3xl">
          <p className="section-label mb-4">Leadership</p>
          <h3 className="font-heading text-4xl text-white mb-5">IN-HOUSE DESIGN EFFICIENCY</h3>
          <p className="text-[#D1D5DB] text-sm leading-relaxed font-body mb-3">
            We are a team of experienced Architects, Designers and Engineers — all extremely trained with past exposure to industry professionals.
          </p>
          <p className="text-[#9CA3AF] text-sm leading-relaxed font-body">
            Our team maintains subtle accessibility and working relationships, assuring our clients full-time attention whenever they need our help.
          </p>
        </div>

        {/* Director Cards — horizontal */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">

          {/* Director 1 — Prasad Kadam */}
          <div className="border border-[#4A4A4A] bg-[#1A1A1A] p-7 flex flex-col h-full" style={{ clipPath:'polygon(0 0,100% 0,100% calc(100% - 20px),calc(100% - 20px) 100%,0 100%)' }}>
            <div className="flex items-start gap-5 mb-7">
              <div className="w-14 h-14 bg-[#C41E3A] flex items-center justify-center shrink-0">
                <span className="font-heading text-white text-xl">PK</span>
              </div>
              <div>
                <h4 className="font-heading text-2xl text-white">PRASAD KADAM</h4>
                <p className="text-[#C41E3A] text-xs uppercase tracking-widest font-body mt-0.5">Founder &amp; Director</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {[{ lb:'DCE', d:'Diploma in Civil Engg.' },{ lb:'BE', d:'Bachelor of Engineering' },{ lb:'MBA-CPM', d:'MBA — Construction PM' },{ lb:'4+ Years', d:'Construction PM Experience' }].map(({ lb, d }) => (
                <div key={lb} className="border border-[#4A4A4A] p-3">
                  <div className="font-heading text-base text-[#C41E3A]">{lb}</div>
                  <div className="text-[#9CA3AF] text-xs font-body mt-0.5">{d}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Director 2 — Update name below */}
          <div className="border border-[#4A4A4A] bg-[#1A1A1A] p-7 flex flex-col h-full" style={{ clipPath:'polygon(0 0,100% 0,100% calc(100% - 20px),calc(100% - 20px) 100%,0 100%)' }}>
            <div className="flex items-start gap-5 mb-7">
              <div className="w-14 h-14 bg-[#C41E3A] flex items-center justify-center shrink-0">
                <span className="font-heading text-white text-xl">PS</span>
              </div>
              <div>
                {/* TODO: Replace with actual director name */}
                <h4 className="font-heading text-2xl text-white">AR. PRAJWAL SHELKE</h4>
                <p className="text-[#C41E3A] text-xs uppercase tracking-widest font-body mt-0.5">Co-Founder &amp; Director</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {[
                { lb:'B.Arch', d:'Bachelor of Architecture' },
                { lb:'5.5+ Yrs', d:'Work Experience' },
                { lb:'Architecture', d:'Residential · Bungalows · Villas · Highrise · Schools' },
                { lb:'Interiors', d:'Residential · Commercial · Retail · Hospitality' },
              ].map(({ lb, d }) => (
                <div key={lb} className="border border-[#4A4A4A] p-3">
                  <div className="font-heading text-base text-[#C41E3A]">{lb}</div>
                  <div className="text-[#9CA3AF] text-xs font-body mt-0.5">{d}</div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#4A4A4A] pt-4">
              <p className="text-[#6B7280] text-[10px] uppercase tracking-widest font-body mb-2">Specialisations</p>
              <div className="flex flex-wrap gap-1.5">
                {['Sustainable Architecture', 'Vernacular Architecture', 'Furniture Design'].map((s) => (
                  <span key={s} className="text-[10px] font-body text-[#9CA3AF] border border-[#4A4A4A] px-2 py-0.5">{s}</span>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </section>
);

/* ---------- PROCESS ---------- */
const ProcessSection = () => (
  <section id="process" className="py-24 bg-[#0A0A0A]" data-testid="process-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-6">
        <div>
          <p className="section-label mb-4">Methodology</p>
          <h2 className="font-heading text-5xl lg:text-6xl text-white">OUR PROCESS</h2>
        </div>
        <p className="text-[#D1D5DB] text-sm font-body max-w-md">
          We follow the <strong className="text-white">PDCA — Plan, Do, Check, Act</strong> principle for continuous improvement and consistent quality delivery.
        </p>
      </div>

      {/* PDCA Flow */}
      <div className="flex items-center justify-between sm:justify-center sm:gap-4 md:gap-8 mb-12 overflow-x-auto pb-4 max-w-full">
        {PDCA.map(({ phase, label, color }, i) => (
          <React.Fragment key={phase}>
            <div className="flex flex-col items-center shrink-0">
              <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 border-2 flex items-center justify-center shrink-0" style={{ borderColor: color }}>
                <span className="font-heading text-xl sm:text-2xl md:text-4xl" style={{ color }}>{label}</span>
              </div>
              <p className="font-heading text-[10px] sm:text-xs text-[#9CA3AF] text-center mt-2 max-w-[65px] leading-tight">{phase}</p>
            </div>
            {i < PDCA.length - 1 && (
              <div className="flex items-center px-1 sm:px-3 md:px-5 shrink-0">
                <div className="w-3 sm:w-8 md:w-12 h-px bg-[#4A4A4A]" />
                <div className="w-0 h-0 border-t-[4px] border-b-[4px] border-l-[5px] sm:border-t-[5px] sm:border-b-[5px] sm:border-l-[6px] border-t-transparent border-b-transparent border-l-[#4A4A4A]" />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* PDCA Detail Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#4A4A4A]" data-testid="pdca-grid">
        {PDCA.map(({ number, phase, label, tagline, items, color }) => (
          <div key={phase} className="bg-[#0A0A0A] p-8 hover:bg-[#1A1A1A] transition-colors" data-testid={`pdca-${label.toLowerCase()}`}>
            <div className="flex items-center gap-4 mb-5">
              <div className="w-11 h-11 border-2 flex items-center justify-center shrink-0" style={{ borderColor: color }}>
                <span className="font-heading text-xl" style={{ color }}>{label}</span>
              </div>
              <div>
                <div className="text-[#6B7280] text-xs font-body">{number}</div>
                <h3 className="font-heading text-xl text-white">{phase}</h3>
              </div>
            </div>
            <p className="text-[#C41E3A] text-xs uppercase tracking-widest font-body mb-4">{tagline}</p>
            <div className="h-px bg-[#4A4A4A] mb-4" />
            <ul className="space-y-2">
              {items.map(item => (
                <li key={item} className="flex items-start gap-2.5 text-[#D1D5DB] text-xs font-body">
                  <span className="w-1 h-1 bg-[#C41E3A] rounded-full mt-1.5 shrink-0" />{item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- PEB ---------- */
const PEBSection = () => (
  <section id="peb" className="py-24 bg-[#0D0D0D] border-y border-[#4A4A4A]" data-testid="peb-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      {/* Intro */}
      <div className="mb-16">
        <p className="section-label mb-4">Pre-Engineered Buildings</p>
        <h2 className="font-heading text-5xl lg:text-6xl text-white mb-6">PEB SOLUTIONS</h2>
        <p className="text-[#D1D5DB] text-base font-body max-w-2xl">
          Factory-fabricated, rapidly assembled and cost-optimised. PEB is a core competency at Kadam Patil Projects — delivering faster, leaner industrial structures.
        </p>
      </div>

      {/* Comparison */}
      <div className="mb-20" data-testid="peb-comparison">
        <p className="section-label mb-5">Comparison</p>
        <h3 className="font-heading text-4xl text-white mb-10">PEB VS CONVENTIONAL CONSTRUCTION</h3>

        <div className="hidden lg:grid grid-cols-12 gap-0 mb-1">
          <div className="col-span-3" />
          <div className="col-span-5 bg-[#C41E3A]/10 border border-[#C41E3A] px-6 py-2">
            <span className="font-heading text-xs text-[#C41E3A] tracking-widest">PRE-ENGINEERED BUILDING</span>
          </div>
          <div className="col-span-4 bg-[#1A1A1A] border border-[#4A4A4A] px-6 py-2 ml-px">
            <span className="font-heading text-xs text-[#9CA3AF] tracking-widest">CONVENTIONAL</span>
          </div>
        </div>

        <div className="space-y-px">
          {COMPARISON.map(({ param, peb, conv, pebWins }, i) => (
            <div key={param} className="grid grid-cols-2 lg:grid-cols-12 gap-px bg-[#4A4A4A]" data-testid={`comparison-row-${i}`}>
              <div className="col-span-2 lg:col-span-3 bg-[#1A1A1A] px-6 py-5 flex items-center">
                <h4 className="font-heading text-sm text-white">{param}</h4>
              </div>
              <div className={`col-span-1 lg:col-span-5 bg-[#0D0D0D] px-4 py-5 sm:px-6 flex items-start gap-3 border-l-2 ${pebWins ? 'border-[#C41E3A]' : 'border-[#4A4A4A]'}`}>
                <span className={`mt-0.5 shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${pebWins ? 'bg-[#C41E3A]' : 'bg-[#4A4A4A]'}`}>
                  {pebWins ? <Check size={10} strokeWidth={2.5} className="text-white"/> : <X size={10} strokeWidth={2.5} className="text-white"/>}
                </span>
                <div className="flex-1">
                  <div className="lg:hidden text-[8px] sm:text-[9px] font-heading tracking-widest text-[#C41E3A] mb-1.5 uppercase">PEB</div>
                  <p className="text-[#D1D5DB] text-xs sm:text-sm font-body leading-relaxed">{peb}</p>
                </div>
              </div>
              <div className="col-span-1 lg:col-span-4 bg-[#0A0A0A] px-4 py-5 sm:px-6 flex items-start gap-3">
                <span className={`mt-0.5 shrink-0 w-4 h-4 rounded-full flex items-center justify-center ${!pebWins ? 'bg-[#C41E3A]' : 'bg-[#4A4A4A]'}`}>
                  {!pebWins ? <Check size={10} strokeWidth={2.5} className="text-white"/> : <X size={10} strokeWidth={2.5} className="text-white"/>}
                </span>
                <div className="flex-1">
                  <div className="lg:hidden text-[8px] sm:text-[9px] font-heading tracking-widest text-[#9CA3AF] mb-1.5 uppercase">CONVENTIONAL</div>
                  <p className="text-[#9CA3AF] text-xs sm:text-sm font-body leading-relaxed">{conv}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 bg-[#C41E3A]/10 border border-[#C41E3A] p-5 flex items-center gap-4">
          <div className="font-heading text-4xl text-[#C41E3A]">6/8</div>
          <p className="text-[#D1D5DB] text-sm font-body">Parameters where PEB outperforms conventional construction — the superior choice for industrial, warehouse and large-span projects.</p>
        </div>
      </div>

      {/* Typical Frames */}
      <div data-testid="typical-frames">
        <p className="section-label mb-4">Technical Reference</p>
        <h3 className="font-heading text-4xl text-white mb-3">TYPES OF TYPICAL FRAMES</h3>
        <p className="text-[#9CA3AF] text-sm font-body mb-12 max-w-2xl">
          Structural frame typologies used in PEB construction. Each type is selected based on span requirements, load conditions and functional use.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#4A4A4A]">
          {FRAMES.map(({ code, name, desc, svg }) => (
            <div key={code} className="bg-[#0A0A0A] p-5 hover:bg-[#1A1A1A] transition-colors group" data-testid={`frame-${code.toLowerCase().replace(/\s+/g,'-')}`}>
              <div className="bg-[#050505] border border-[#2A2A2A] p-3 mb-4 group-hover:border-[#C41E3A]/30 transition-colors min-h-[100px] flex items-center">
                {svg}
              </div>
              <span className="inline-block bg-[#C41E3A] text-white text-xs font-heading tracking-widest px-2 py-0.5 mb-2">{code}</span>
              <h4 className="font-heading text-sm text-white mb-1">{name}</h4>
              <p className="text-[#9CA3AF] text-xs font-body leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- PORTFOLIO — extracted to /components/portfolio/PortfolioSection.jsx ---------- */

/* ---------- CONTACT ---------- */
const ContactSection = () => {
  const formRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true); setStatus(null);
    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        formRef.current,
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      setStatus('success');
      formRef.current.reset();
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#0A0A0A] border-t border-[#4A4A4A]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-14">
          <p className="section-label mb-4">Reach Out</p>
          <h2 className="font-heading text-5xl lg:text-6xl text-white">GET IN TOUCH</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#4A4A4A]">
          {/* Form */}
          <div className="lg:col-span-7 bg-[#0A0A0A] p-8 lg:p-10" data-testid="contact-form-section">
            <p className="section-label mb-4">Send a Message</p>
            <h3 className="font-heading text-3xl text-white mb-7">LET'S DISCUSS YOUR PROJECT</h3>
            <form ref={formRef} onSubmit={handleSubmit} data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2 font-body">Full Name *</label>
                  <input type="text" name="from_name" required placeholder="Your full name"
                    className="w-full bg-[#1A1A1A] border border-[#4A4A4A] text-white text-sm font-body px-4 py-3.5 focus:outline-none focus:border-[#C41E3A] placeholder-[#6B7280] transition-colors"
                    data-testid="contact-input-name" />
                </div>
                <div>
                  <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2 font-body">Company</label>
                  <input type="text" name="company" placeholder="Company name"
                    className="w-full bg-[#1A1A1A] border border-[#4A4A4A] text-white text-sm font-body px-4 py-3.5 focus:outline-none focus:border-[#C41E3A] placeholder-[#6B7280] transition-colors"
                    data-testid="contact-input-company" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2 font-body">Email Address *</label>
                  <input type="email" name="reply_to" required placeholder="your@email.com"
                    className="w-full bg-[#1A1A1A] border border-[#4A4A4A] text-white text-sm font-body px-4 py-3.5 focus:outline-none focus:border-[#C41E3A] placeholder-[#6B7280] transition-colors"
                    data-testid="contact-input-email" />
                </div>
                <div>
                  <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2 font-body">Phone Number</label>
                  <input type="tel" name="phone" placeholder="+91 XXXXX XXXXX"
                    className="w-full bg-[#1A1A1A] border border-[#4A4A4A] text-white text-sm font-body px-4 py-3.5 focus:outline-none focus:border-[#C41E3A] placeholder-[#6B7280] transition-colors"
                    data-testid="contact-input-phone" />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-[#9CA3AF] text-xs uppercase tracking-widest mb-2 font-body">Message *</label>
                <textarea name="message" required rows={5} placeholder="Describe your project requirements..."
                  className="w-full bg-[#1A1A1A] border border-[#4A4A4A] text-white text-sm font-body px-4 py-3.5 focus:outline-none focus:border-[#C41E3A] placeholder-[#6B7280] transition-colors resize-none"
                  data-testid="contact-input-message" />
              </div>
              <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                className="flex items-center gap-3 bg-[#C41E3A] text-white text-xs font-bold uppercase tracking-[0.2em] px-10 py-4 clip-btn hover:bg-white hover:text-[#0A0A0A] transition-colors disabled:opacity-60">
                {sending ? <><Loader size={14} className="animate-spin"/> Sending...</> : <><Send size={14} strokeWidth={1.5}/> Send Message</>}
              </button>
              {status === 'success' && (
                <div className="mt-4 border border-green-500/40 bg-green-500/10 px-5 py-4 text-green-400 text-sm font-body" data-testid="contact-success-msg">
                  Message sent successfully! We'll get back to you soon.
                </div>
              )}
              {status === 'error' && (
                <div className="mt-4 border border-red-500/40 bg-red-500/10 px-5 py-4 text-sm font-body" data-testid="contact-error-msg">
                  <p className="text-[#C41E3A] font-medium mb-1">Unable to send message.</p>
                  <p className="text-[#9CA3AF]">Email us at <a href="mailto:info@kadampatilprojects.com" className="text-white hover:text-[#C41E3A] transition-colors">info@kadampatilprojects.com</a> or call <a href="tel:+919881048008" className="text-white hover:text-[#C41E3A] transition-colors">+91 9881 048 008</a></p>
                </div>
              )}
            </form>
          </div>

          {/* Info */}
          <div className="lg:col-span-5 bg-[#1A1A1A] p-8 lg:p-10" data-testid="contact-info-section">
            <p className="section-label mb-4">Contact Details</p>
            <h3 className="font-heading text-3xl text-white mb-8">REACH DIRECTLY</h3>
            <div className="space-y-5 mb-10">
              {[
                { icon: Phone, label: 'Phone', value: '+91 9881 048 008', href: 'tel:+919881048008' },
                { icon: Mail, label: 'Email', value: 'info@kadampatilprojects.com', href: 'mailto:info@kadampatilprojects.com' },
                { icon: Globe, label: 'Website', value: 'www.kadampatilprojects.com', href: 'http://www.kadampatilprojects.com' },
                { icon: MapPin, label: 'Address', value: 'Jeur, Ahmednagar, Maharashtra 414601', href: null },
              ].map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="flex items-start gap-4 group">
                  <div className="w-9 h-9 border border-[#4A4A4A] flex items-center justify-center shrink-0 group-hover:border-[#C41E3A] transition-colors mt-0.5">
                    <Icon size={14} strokeWidth={1.5} className="text-[#C41E3A]" />
                  </div>
                  <div>
                    <p className="text-[#6B7280] text-xs uppercase tracking-widest font-body mb-1">{label}</p>
                    {href ? (
                      <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer"
                        className="text-[#D1D5DB] text-sm font-body hover:text-white transition-colors break-all" data-testid={`contact-${label.toLowerCase()}`}>{value}</a>
                    ) : (
                      <p className="text-[#D1D5DB] text-sm font-body" data-testid={`contact-${label.toLowerCase()}`}>{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="h-px bg-[#4A4A4A] mb-8" />
            <div className="space-y-2 mb-8">
              <p className="text-[#C41E3A] text-xs uppercase tracking-widest font-body mb-4">Working Hours</p>
              {[['Monday — Friday','9:00 AM — 6:00 PM'],['Saturday','9:00 AM — 2:00 PM'],['Sunday','Closed']].map(([d,h]) => (
                <div key={d} className="flex justify-between text-sm font-body">
                  <span className="text-[#9CA3AF]">{d}</span>
                  <span className={d === 'Sunday' ? 'text-[#6B7280]' : 'text-[#D1D5DB]'}>{h}</span>
                </div>
              ))}
            </div>
            <div className="border-l-2 border-[#C41E3A] pl-5">
              <p className="text-white font-heading text-xl mb-0.5">WHERE INNOVATION</p>
              <p className="text-[#C41E3A] font-heading text-xl">MEETS INTEGRITY</p>
              <p className="text-[#9CA3AF] text-xs font-body mt-2 uppercase tracking-widest">Kadam Patil Projects</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================================
//  LANDING PAGE
// ============================================================
const LandingPage = () => (
  <div className="bg-[#0A0A0A]" data-testid="landing-page">
    <HeroSection />
    <TickerSection />
    <ServicesSection />
    <AboutSection />
    <ProcessSection />
    <PEBSection />
    <PortfolioSection />
    <ContactSection />
  </div>
);

export default LandingPage;
